import { connect } from 'react-redux';
import { reduxForm, formValueSelector } from 'redux-form';
import Joi from 'joi-full';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps } from 'recompose';
import { isEmpty, get, sortBy } from 'lodash';
import { t } from '../../../../../../modules/infra/i18n';
import Spinner from '../../../../../common/Spinner';

import { syncValidateData } from '../../../../../common/validator';
import { getShopBrand, getShopBrandId, getShops, isBrandViewActive } from '../../../../../auth/selectors';
import { saveShopifySettings } from '../../actions';
import withToggle from '../../../../../common/hoc/withToggle';
import { Logger } from '../../../../../../modules/infra/observability/logger';
import ShopifySettingsForm from './ShopifySettingsForm';
import withNotifications from '../../../../../common/withNotifications';
import { fetchMatches, fetchSettings, fetchRemoteLocations } from '../../reducer/actions';
import { MATCHES_PENDING_STATUS } from '../../../constants';

const SHOPIFY_SETTINGS_FORM = 'shopifySettingsForm';

const schema = Joi.object().keys({
  locationId: Joi.string(),
  remoteLocationId: Joi.number().required(),
  primarySource: Joi.string().valid('squire', 'vendor'),
  enablePriceSync: Joi.bool(),
});

const validate = (model) => syncValidateData(model, schema);
const selector = formValueSelector(SHOPIFY_SETTINGS_FORM);

const initialValues = {
  enablePriceSync: false,
  primarySource: 'squire',
  remoteLocationId: '',
};

const generateAddressStringFromObject = (address) => {
  if (!address) {
    return '';
  }

  return [address.street, address.street2, address.city, address.state, address.zip].filter(Boolean).join(', ');
};

const generateShopAddressFrom = (shop) => {
  if (!shop || !shop.address) {
    return '';
  }

  return generateAddressStringFromObject(shop.address);
};

const getSquireLocations = (state, brandId, isBrandUser) => {
  const shopsLocations = getShops(state).map((shopItem) => ({
    label: generateShopAddressFrom(shopItem),
    value: shopItem.id,
  }));

  // unallocated - brand level inventory, available only for brand user
  if (isBrandUser) {
    return [
      {
        label: t('settings:integrations.shopify.unallocated_inventory'),
        value: brandId,
      },
      ...shopsLocations,
    ];
  }

  return shopsLocations;
};

const mapStateToProps = (state) => {
  const brandId = getShopBrandId(state);
  const isBrandUser = isBrandViewActive(state);
  const squireLocations = getSquireLocations(state, brandId, isBrandUser);
  const remoteLocations = state.shopify.shopifyLocations.locationsToOptions;
  const remoteLocationsSorted = sortBy(remoteLocations, (remoteLocation) => remoteLocation.id);
  const initialFormValues = !isEmpty(state.shopify.shopifySettings.settings)
    ? state.shopify.shopifySettings.settings
    : {
      ...initialValues,
      locationId: get(squireLocations, '[0].value', ''),
      remoteLocationId: get(remoteLocationsSorted, '[0].value', ''),
    };

  return {
    brandId,
    brand: getShopBrand(state),
    shops: getShops(state),
    locations: squireLocations,
    loading: state.shopify.shopifyLocations.loading,
    initialValues: initialFormValues,
    selectedShopId: selector(state, 'locationId'),
    shopifySettings: state.shopify.shopifySettings.settings,
    remoteLocations: remoteLocationsSorted,
  };
};

const mapDispatchToProps = {
  fetchRemote: fetchRemoteLocations,
  fetchSettings,
  fetchMatches,
};

export default compose(
  withNotifications,
  withToggle('confirmation', false),
  connect(mapStateToProps, mapDispatchToProps),
  withProps((props) => {
    const selectedShop = props.shops.find((shop) => shop.id === props.selectedShopId);
    const brandAddress = get(props, 'brand.address', {}) || {};
    const brandAddressString = generateAddressStringFromObject(brandAddress);
    const shopAddressString = generateShopAddressFrom(selectedShop);
    const shopName = get(selectedShop, 'name', '');
    const connectedToName =
      props.selectedShopId === props.brandId ? t('settings:integrations.shopify.all_shops') : shopName;
    const connectedTo = connectedToName
      ? t('settings:integrations.shopify.connected_to', { shopName: connectedToName })
      : undefined;

    return {
      brandAddressString,
      connectedTo,
      shopName,
      shopAddressString,
    };
  }),
  withHandlers({
    saveChanges: (props) => async (values) => {
      const brandName = get(props, 'brand.name', '') || '';

      const brandLocationMeta = {
        locationId: props.brand.id,
        locationAddress: props.brandAddressString,
        locationName: brandName,
        locationType: 'brand',
      };

      const shopLocationMeta = {
        locationId: values.locationId,
        locationAddress: props.shopAddressString,
        locationName: props.shopName,
        locationType: 'shop',
      };

      // if value === '0' location is unallocated
      const locationMeta = values.locationId === props.brand.id ? brandLocationMeta : shopLocationMeta;

      const data = {
        vendor: 'shopify',
        accountId: props.brandId,
        settings: {
          ...locationMeta,
          remoteLocationId: values.remoteLocationId,
          primarySource: values.primarySource,
          enablePriceSync: values.enablePriceSync,
          shopIdentifier: get(props.shopifySettings, 'shopIdentifier'),
        },
      };

      try {
        await saveShopifySettings(props.brandId, data);

        props.addNotification({
          title: t('common:success_title'),
          level: 'success',
          message: t('settings:integrations.shopify.settings_saved_successfully'),
        });
      } catch (error) {
        Logger.error('Save Shopify settings', {
          error,
          action: 'Save Shopify settings',
        });
      }
    },
  }),
  withHandlers({
    onSubmit: (props) => async (values) => {
      const removeLocationChanged = props.shopifySettings.remoteLocationId !== values.remoteLocationId;
      const locationChanged = props.shopifySettings.locationId !== values.locationId;
      const firstSave = isEmpty(props.shopifySettings);
      const needToConfirm = !props.confirmationIsOn && (removeLocationChanged || locationChanged) && !firstSave;

      if (needToConfirm) {
        props.confirmationOn();

        return;
      }

      if (props.confirmationIsOn) {
        props.confirmationOff();
      }

      await props.saveChanges(values);

      props.fetchSettings(props.brandId);

      // for the modal we reFetching matches after close
      if (props.closeModal) {
        props.closeModal();
        props.fetchMatches(props.brandId, props.shopifySettings, MATCHES_PENDING_STATUS);
      }
    },
  }),
  reduxForm({
    form: SHOPIFY_SETTINGS_FORM,
    enableReinitialize: true,
    validate,
  }),
  lifecycle({
    componentDidMount() {
      this.props.fetchRemote(this.props.brandId);
    },
  }),
  branch((props) => props.loading, renderComponent(Spinner))
)(ShopifySettingsForm);
